import { useEffect, useState } from "react";
import { ShieldAlert, LogOut, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";

export const leaveOpsSession = (path = "/ops/tickets") => {
  localStorage.setItem("pressops_token", localStorage.getItem("pressops_ops_token") || "");
  localStorage.removeItem("pressops_ops_token");
  localStorage.removeItem("pressops_user");
  window.location.href = path;
};

const clock = (s) => `${Math.floor(s / 3600)}h ${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}m ${String(s % 60).padStart(2, "0")}s`;

function BreakGlassDialog({ open, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [ack, setAck] = useState(false);
  const submit = async () => {
    try { await api.post("/ops/session/break-glass", { reason, acknowledge: ack }); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="break-glass-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-rose-600" />Access payroll and HR data</DialogTitle>
          <DialogDescription>Payroll and HR access is logged at elevated severity with a break-glass marker, and System Admins can review every request.</DialogDescription></DialogHeader>
        <label className="block space-y-1.5"><span className="text-sm font-medium text-slate-700">Confirm reason (min 10 characters)</span>
          <Input data-testid="break-glass-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Payslip query raised by the tenant admin" /></label>
        <label className="flex items-start gap-2 text-sm text-slate-700"><input type="checkbox" data-testid="break-glass-ack" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-0.5" />
          I understand this access is logged at elevated severity.</label>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="break-glass-confirm" disabled={reason.trim().length < 10 || !ack} onClick={submit} className="bg-rose-600 hover:bg-rose-700">Activate break-glass</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function OpsSessionBanner({ s }) {
  const [left, setLeft] = useState(() => Math.max(0, Math.floor((new Date(s.expires_at) - Date.now()) / 1000)));
  const [dlg, setDlg] = useState(false);
  const sysAdmin = s.role === "platform_admin";
  const home = sysAdmin ? "/platform/tickets" : "/ops/tickets";
  useEffect(() => {
    const t = setInterval(() => {
      const v = Math.max(0, Math.floor((new Date(s.expires_at) - Date.now()) / 1000));
      setLeft(v);
      if (v === 0) { toast.error("Ops session expired"); leaveOpsSession(home); }
    }, 1000);
    return () => clearInterval(t);
  }, [s.expires_at]); // eslint-disable-line react-hooks/exhaustive-deps
  const reload = () => { localStorage.removeItem("pressops_user"); window.location.reload(); };
  const toggle = async (on) => {
    if (on) return setDlg(true);
    try { await api.post("/ops/session/break-glass/off"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const end = async () => { try { await api.post("/ops/session/end"); } finally { leaveOpsSession(home); } };
  return (
    <div data-testid="ops-session-banner" className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b px-6 py-2.5 text-sm po-tone-info">
      <span className="font-semibold">Ops session · {s.tenant_name}</span>
      {s.ticket_number && <span className="po-chip po-tone-neutral !normal-case" data-testid="ops-session-ticket">{s.ticket_number}</span>}
      {s.role === "support_l1" && <span className="po-chip po-tone-warning !normal-case" data-testid="ops-session-viewonly">View-only (L1)</span>}
      {sysAdmin && <span className="po-chip po-tone-info !normal-case" data-testid="ops-session-sysadmin">System Admin</span>}
      {s.role === "support_l2" && <span className="po-chip po-tone-info !normal-case" data-testid="ops-session-l2">Support Specialist (L2)</span>}
      <span className="min-w-0 flex-1 truncate text-xs" title={s.reason}>Reason: {s.reason}</span>
      <span className="inline-flex items-center gap-1 tabular-nums" data-testid="ops-time-remaining"><Clock className="h-4 w-4" />{clock(left)}</span>
      <label className="inline-flex items-center gap-2 text-xs font-medium" hidden={!sysAdmin && (s.role || "platform_ops") !== "platform_ops"}>
        <Switch data-testid="break-glass-toggle" checked={!!s.break_glass} onCheckedChange={toggle} />Access payroll and HR data
        {s.break_glass && <span className="po-chip po-tone-danger !normal-case" data-testid="break-glass-active">Break-glass on</span>}</label>
      <Button size="sm" variant="outline" data-testid="ops-end-session" onClick={end}><LogOut className="mr-1 h-3.5 w-3.5" />End session</Button>
      <BreakGlassDialog open={dlg} onClose={() => setDlg(false)} onDone={reload} />
    </div>
  );
}
